import { useClientPortalAPI } from "@certn/api";
import { Text } from "@certn/cascadia";
import { Report, ReportData } from "@certn/report";

import { Card, SectionMessage } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useTranslation } from "react-i18next";
import constants from "../../../../constants";
import { useUser } from "../../../hooks/useUser";
import { EmailVerificationBanner } from "../../dashboard/EmailVerificationBanner";

interface Props {
  caseId: string | undefined;
}

const EMPTY_STATE = {
  case_details: {
    case_id: "",
    short_id: "",
    date_ordered: "",
  },
  checks: [],
  applicant_info: {
    email: "",
    current_name: {
      given_name: "",
      additional_name: "",
      family_name: "",
    },
    current_location: {
      street_address: "",
      unit_number: "",
      locality: "",
      administrative_division_1: "",
      country: "",
      postal_code: "",
    },
    position_location: {
      type: "",
      address: null,
    },
    date_of_birth: "",
  },
  date_completed: "",
  date_generated: "",
};

export function ReportTab({ caseId }: Props) {
  const { t } = useTranslation();

  const { data, isLoading, error } = useClientPortalAPI(
    `/api/internal/client-portal/report/${caseId}/`
  );

  const { details: userDetails } = useUser();

  const { clientPortalRequireEmailVerification } = useFlags();

  if (
    clientPortalRequireEmailVerification &&
    userDetails?.auth0_email_verified === false
  ) {
    return (
      <Card
        className={styles.cardContainer}
        type="large"
        responsive
        testID="report-card"
        border="border-base"
      >
        <Text style="global/heading-large">{t("CASE Report", "Report")}</Text>
        <EmailVerificationBanner
          type="danger"
          title={t("Please verify your email to view your case results.")}
          message={t(
            "Until your email is verified, your case results will remain hidden."
          )}
        />
      </Card>
    );
  }

  if (error) {
    return (
      <SectionMessage
        type="danger"
        icon="error"
        sectionTitle={t("Error loading report")}
      >
        {t("Unable to load the report for this case, please try again later.")}
      </SectionMessage>
    );
  }

  return (
    <div data-testid="report">
      <Report
        clickwrapAccessID={constants.VITE_CLICKWRAP_ACCESS_ID}
        watermark={constants.VITE_USE_WATERMARKS_ON_REPORTS}
        reportData={data ? (data?.report_data as ReportData) : EMPTY_STATE}
        appIdentifier="CLIENT_PORTAL"
        responsive
        fulfillmentNotes={data?.case?.fulfillment_notes || []}
        isLoading={isLoading}
      />
    </div>
  );
}

const styles = {
  cardContainer: css({
    padding: "var(--space-5) var(--space-4)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
};
